import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Clock, MapPin, Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/royal-rewards", label: "Royal Rewards" },
  { to: "/contact", label: "Contact" },
];

function NavItems({ onClick }: { onClick?: () => void }) {
  return (
    <>
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end onClick={onClick} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) => cn("inline-flex min-h-[48px] items-center px-3 text-[15px] transition-colors duration-200 hover:text-gold", isActive ? "text-gold" : "text-mist/85")}>
          {n.label}
        </NavLink>
      ))}
    </>
  );
}

export default function SiteLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className="flex min-h-screen flex-col bg-ink font-body text-mist">
      <div className="hidden border-b border-gold/20 bg-aubergine md:block" data-testid="site-topbar">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 py-2 text-[14px] text-mist/85 md:px-10">
          <span className="flex items-center gap-2"><MapPin size={15} className="text-gold" /> DeSoto County · Greater Memphis · Oxford · Nashville</span>
          <span className="flex items-center gap-6">
            <span className="flex items-center gap-2"><Phone size={15} className="text-gold" /> [Phone number placeholder]</span>
            <span className="flex items-center gap-2"><Clock size={15} className="text-gold" /> All times Central</span>
          </span>
        </div>
      </div>
      <header className="sticky top-0 z-40 border-b border-gold/20 bg-ink/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3 md:px-10">
          <Logo />
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            <NavItems />
            <Link to="/book" data-testid="nav-book-cta" className="ml-4 inline-flex min-h-[48px] items-center rounded-full bg-plum px-6 font-medium text-mist transition-colors duration-200 hover:bg-orchid">Book a Ride</Link>
          </nav>
          <button className="grid h-12 w-12 place-items-center rounded-full border border-gold/40 text-gold lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <nav className="flex flex-col border-t border-gold/20 px-5 pb-5 lg:hidden" aria-label="Mobile">
            <NavItems onClick={() => setOpen(false)} />
            <Link to="/book" onClick={() => setOpen(false)} data-testid="nav-mobile-book-cta" className="mt-3 inline-flex min-h-[48px] items-center justify-center rounded-full bg-plum font-medium text-mist">Book a Ride</Link>
          </nav>
        )}
      </header>
      <main className="flex-1"><Outlet /></main>
      <footer className="border-t border-gold/30 bg-[#0b0710]">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:grid-cols-2 md:px-10 lg:grid-cols-4">
          <div className="flex flex-col gap-4">
            <Logo />
            <p className="max-w-xs text-[14px] leading-relaxed text-mist/70">More Than a Ride. An Experience. A Familiar Face. A Personal Touch. A Better Ride.</p>
          </div>
          <div>
            <p className="eyebrow mb-4">Quick links</p>
            <ul className="flex flex-col">
              {NAV.map((n) => <li key={n.to}><Link to={n.to} className="inline-flex min-h-[44px] items-center text-[15px] text-mist/80 hover:text-gold">{n.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <p className="eyebrow mb-4">Services</p>
            <ul className="flex flex-col gap-3 text-[15px] text-mist/80">
              <li>Standard</li><li>Airport</li><li>Nashville Express</li><li>Custom Long Distance</li>
            </ul>
          </div>
          <div className="flex flex-col gap-1">
            <p className="eyebrow mb-3">Ride with us</p>
            <p className="text-[15px] text-mist/80">[Phone number placeholder]</p>
            <p className="mt-2 text-[15px] leading-relaxed text-mist/80">DeSoto County · Greater Memphis · Oxford · Nashville</p>
            <Link to="/book" className="inline-flex min-h-[48px] items-center text-gold hover:underline" data-testid="footer-book-link">Request a ride</Link>
            <Link to="/sign-in" className="inline-flex min-h-[48px] items-center text-[14px] text-mist/60 hover:text-gold" data-testid="footer-owner-login">Owner login</Link>
          </div>
        </div>
        <div className="gold-rule mx-auto max-w-7xl" />
        <p className="mx-auto max-w-7xl px-5 py-6 text-[14px] text-mist/55 md:px-10">© {new Date().getFullYear()} KMT Private Rides</p>
      </footer>
    </div>
  );
}
